import type { ArgusGatewayEvent } from "../../types/argus-events";
import { matchRouteContext, type RouteContextKey } from "./route-registry";

/**
 * A resource the agent has touched during the current session — surfaced
 * in the `/argus` page's right-hand "Related Resources" panel.
 *
 * We derive resources from `navigate` tool calls (the agent's primary
 * signal for "look at this thing"), so the list stays in sync with the
 * event buffer without an extra reducer.
 */
export interface RelatedResource {
  id: string;
  type: RouteContextKey | "navigation";
  label: string;
  path: string;
  reason?: string | null;
  timestamp: string;
}

/**
 * Extract a `RelatedResource` from a single gateway event, or return null
 * if the event doesn't represent a resource-worthy action.
 *
 * Only listens to successful `navigate` tool completions. Bash URL
 * scraping is intentionally out of scope for v1 — too noisy and
 * brittle; `navigate` is the agent's explicit advertisement signal.
 */
export function extractResource(event: ArgusGatewayEvent): RelatedResource | null {
  if (event.type !== "tool_execution_end") return null;
  if (event.toolName !== "navigate") return null;
  if (event.isError) return null;

  const resultPayload = event.result as
    | { details?: { path?: string; label?: string; reason?: string | null } }
    | undefined;
  const details = resultPayload?.details;
  if (!details?.path) return null;

  const match = matchRouteContext(details.path);

  return {
    id: `${event.toolCallId}-${details.path}`,
    type: match?.key ?? "navigation",
    label: details.label ?? match?.label ?? details.path,
    path: details.path,
    reason: details.reason ?? null,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Fold the full event buffer into a deduplicated list of related
 * resources — most recent occurrence wins, ordered by discovery
 * (original index).
 */
export function extractResources(events: ArgusGatewayEvent[]): RelatedResource[] {
  const byPath = new Map<string, RelatedResource>();
  events.forEach((event) => {
    const resource = extractResource(event);
    if (!resource) return;
    byPath.set(resource.path, resource);
  });
  return Array.from(byPath.values());
}
